import { canAccessAdminShell } from '@versports/access'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import {
  know,
  PRE_SESSION_CODE,
  type AnswerValue,
  type ItemDef,
  type QuestionnaireDefinition,
  type ResponseDetail,
} from '../../api/know'
import { ApiError } from '../../api/client'
import type { MeResponse } from '../../api/auth'
import { useAuth } from '../../auth/AuthContext'
import { playerStartRoute } from '../../auth/postLoginRoute'
import { VerSportsLogo } from '@versports/ui'
import type { KnowOutletCtx } from './KnowLayout'
import { SubmitSuccess } from './SubmitSuccess'

/**
 * Per-questionnaire route. Two visual modes:
 *
 *  - **Form mode** (not responded yet OR explicit edit on an editable
 *    questionnaire) — one item per screen with the right input for each
 *    `kind` (likert / single_select / number / text). Likert + single_select
 *    auto-advance on tap; number + text show an explicit "Siguiente" button
 *    (textareas can't auto-advance without surprising the user). Last
 *    screen is the review summary; required items show "Falta" if blank.
 *
 *  - **Detail mode** (already responded, not in edit mode) — plain
 *    confirmation, no scores/chart/answer breakdown. Players don't see their
 *    own psychometric results in-app (devoluciones are handled personally by
 *    the sport psychologist via the admin view — see AdminQuestionnaireDetail),
 *    plus an "Editar respuestas" button when `def.editable` is true. El
 *    catálogo de auto-servicio ya no existe (los cuestionarios se asignan por
 *    invitación y el gate vive en la HomePage), así que el form corre
 *    standalone sin el nudge "próximo pendiente".
 *
 * Al ENVIAR por primera vez en el flujo personal (`/data/:code`), un JUGADOR
 * no ve el detail: sigue derecho a jugar (decisión de producto, mg 2026-09-07
 * — doc 32 §4). Se muestra un cartel de "Listo, guardado" sin botón mientras
 * `refresh()` trae `/auth/me` fresco y el navigate cae en
 * `playerStartRoute(user)`, que hoy es el bridge `/` → lobby del vr-app (los
 * gates que quedan corren ahí, después de "Comenzar"). Staff de club y admins
 * conservan el detail de siempre, y el modo tablet (`/tablet/data/:code`,
 * `exitPath` que arranca con `/tablet`) tampoco cambia: ahí el auto-logout entre
 * jugadores lo maneja `KitLayout` leyendo `state.justSubmitted`.
 */
export function KnowQuestionnaire() {
  const { code = '' } = useParams<{ code: string }>()
  const { revalidate, exitPath, endSession } = useOutletContext<KnowOutletCtx>()
  const { user, refresh } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // Derived from router history state (not local useState): `refresh()`
  // below flips the global auth status through 'hydrating', which makes
  // RequireAuth briefly unmount/remount this whole tree — local state
  // would reset to its initial value on that remount, but `location.state`
  // survives because it's tied to the history entry, not the component.
  const submitState = location.state as
    | { justSubmitted?: boolean; continuing?: boolean }
    | null
  const justSubmitted = Boolean(submitState?.justSubmitted)
  // El jugador ya está en camino a jugar: el árbol se remonta durante el
  // `refresh()` y esta pantalla (también anclada a la history entry) es lo
  // único que se ve hasta que el navigate resuelve.
  const continuing = Boolean(submitState?.continuing)

  // La tablet compartida monta este mismo form en `/tablet/data/:code`; ahí el
  // post-submit lo maneja `KitLayout` (gracias + logout), no nosotros.
  const isKit = (exitPath ?? '').startsWith('/tablet')

  const [def, setDef] = useState<QuestionnaireDefinition | null>(null)
  const [response, setResponse] = useState<ResponseDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setEditing(false)
    know.detail(code)
      .then((data) => {
        if (cancelled) return
        setDef(data.definition)
        setResponse(data.response)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof ApiError ? e.message : 'Error de red.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [code, loadAttempt])

  // Antes que `loading`: el remount que dispara `refresh()` vuelve a pedir el
  // detail, y sin este early-return se vería un "Cargando…" (o un flash del
  // detail) entre el envío y el próximo paso.
  if (continuing) return <ContinueSplash />

  if (loading) return <p className="text-sm text-text-muted">Cargando…</p>
  if (error) return <div className="panel p-lg" role="alert">
    <p className="banner-err">{error}</p>
    <button className="btn-primary" onClick={() => setLoadAttempt(n => n + 1)}>Reintentar</button>
    {endSession && <button className="btn ml-md" onClick={endSession}>Salir</button>}
  </div>
  if (!def) return null

  // El chequeo previo se responde SOLO en el casco (gate del vr-app): abrirlo
  // acá como form daría una segunda vía para el mismo dato por sesión, sin
  // sesión contra la cual ligarlo. La vista de detalle sí existe (lectura).
  const isPreSession = def.code === PRE_SESSION_CODE

  if (response && !editing) {
    return (
      <DetailView
        def={def}
        response={response}
        justSubmitted={justSubmitted}
        onEdit={def.editable && !isPreSession ? () => setEditing(true) : null}
      />
    )
  }

  if (isPreSession) return <PreSessionNotHere />

  return (
    <FormView
      def={def}
      code={code}
      initialAnswers={editing ? response?.answers ?? {} : {}}
      isEdit={editing}
      // `exitPath` lo inyecta el layout que monta el form: `/data` por
      // defecto, `/tablet` en el modo tablet (volver al área personal desde
      // una tablet compartida expondría el perfil del jugador anterior).
      onLeave={() => {
        if (endSession) endSession()
        else navigate(exitPath ?? '/data')
      }}
      onSubmitted={async (saved) => {
        setResponse(saved)
        setEditing(false)
        // Un jugador que acaba de RESPONDER (no editar) en el flujo personal
        // sigue derecho a jugar; el resto se queda en el detail de siempre.
        const continueToPlay = !editing && !isKit && isPlayer(user)
        // Stamp justSubmitted on the history entry itself (see
        // `justSubmitted` above) before `refresh()` below can trigger the
        // RequireAuth remount. Re-navega a la ruta ACTUAL en vez de a
        // `/data/{code}` hardcodeado: en `/data/*` es exactamente lo mismo,
        // pero el modo tablet monta este mismo form en `/tablet/data/{code}` y
        // ahí saltar a `/data` lo sacaría del árbol que maneja el logout
        // entre jugadores (`kit/KitLayout.tsx`).
        navigate(location.pathname, {
          replace: true,
          state: { justSubmitted: true, continuing: continueToPlay },
        })
        void revalidate()
        // Refresh /auth/me so `pending_questionnaires` shrinks as soon as an
        // assigned questionnaire is answered — this is what unblocks the Train
        // button (HomePage + AppNavBar) once all pendings are done.
        if (isKit) return // TabletLayout owns the pending check and recovery.
        const me = await refresh()
        if (!continueToPlay) return
        // Sesión perdida en el medio: `RequireAuth` ya rebota al login, no
        // hay a dónde navegar.
        if (!me) return
        // Defensa contra un `/auth/me` que todavía liste el cuestionario
        // recién enviado: sin esto `playerStartRoute` podría devolvernos a
        // esta misma ruta y el jugador vería el form otra vez.
        const fresh: MeResponse = {
          ...me,
          pending_questionnaires: (me.pending_questionnaires ?? []).filter(
            (q) => q.code !== code,
          ),
        }
        navigate(playerStartRoute(fresh), { replace: true })
      }}
      onCancelEdit={editing ? () => setEditing(false) : null}
    />
  )
}

/**
 * Chequeo previo abierto desde la web sin respuesta que mostrar. No hay form:
 * es obligatorio por sesión y se responde dentro del vr-app, justo antes de
 * entrar (decisión de mg, 2026-09-07).
 */
function PreSessionNotHere() {
  return (
    <section className="flex flex-col gap-md">
      <span className="tag self-start">{PRE_SESSION_CODE}</span>
      <h1 className="m-0">Chequeo previo</h1>
      <p className="m-0 max-w-xl text-sm leading-relaxed text-text-secondary">
        Este chequeo se responde en el casco, antes de cada sesión: son tres
        preguntas sobre cómo llegás hoy y aparecen apenas tocás "Comenzar".
      </p>
      <Link to="/" className="btn-primary self-start no-underline">
        Ir a jugar →
      </Link>
    </section>
  )
}

/**
 * Quién sigue derecho a jugar después de enviar: el JUGADOR. Staff de club
 * (con `club_memberships`) y admins responden cuestionarios para probarlos o
 * para su propio registro, no para entrar al VR, así que se quedan en el
 * detail. Mismo criterio de rol que `postLoginRoute` (a propósito NO se usa
 * `canSeeAdmin`: su bypass de DEV daría "admin" a todo el mundo en local y
 * apagaría este flujo justo donde se lo prueba).
 */
function isPlayer(user: MeResponse | null): boolean {
  if (!user) return false
  if ((user.club_memberships?.length ?? 0) > 0) return false
  if (canAccessAdminShell(user)) {
    return false
  }
  return true
}

/**
 * Confirmación sin botón: se ve mientras `refresh()` trae los gates y el
 * navigate resuelve el próximo paso de la cadena. Sobrevive al remount de
 * `RequireAuth` porque el flag vive en la history entry, no en state local.
 */
function ContinueSplash() {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-md bg-surface p-lg text-center">
      <VerSportsLogo />
      <h1 className="m-0 text-2xl font-bold tracking-tight text-text-primary">
        Listo, guardado
      </h1>
      <p className="m-0 text-base text-text-secondary">Seguimos…</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Form (one question per screen) — mobile-first, supports all item kinds
// ---------------------------------------------------------------------------

function FormView({
  def,
  code,
  initialAnswers,
  isEdit,
  onLeave,
  onSubmitted,
  onCancelEdit,
}: {
  def: QuestionnaireDefinition
  code: string
  initialAnswers: Record<string, AnswerValue>
  isEdit: boolean
  onLeave: () => void
  onSubmitted: (r: ResponseDetail) => void
  onCancelEdit: (() => void) | null
}) {
  const totalItems = def.items.length
  // step === -1 is the standalone intro screen (instructions only, shown
  // once before the first question); step === totalItems is the review.
  const [step, setStep] = useState(-1)
  const [answers, setAnswers] = useState<Record<number, AnswerValue>>(() => {
    const seed: Record<number, AnswerValue> = {}
    for (const [k, v] of Object.entries(initialAnswers)) {
      const id = Number(k)
      if (!Number.isNaN(id) && v !== undefined && v !== null) seed[id] = v
    }
    return seed
  })
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState<ResponseDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const questionRef = useRef<HTMLHeadingElement | null>(null)
  const advanceTimerRef = useRef<number | null>(null)

  const isIntro = step === -1
  const isReview = step === totalItems
  const currentItem = !isIntro && !isReview ? def.items[step] : null

  const requiredCount = useMemo(
    () => def.items.filter((it) => it.required).length,
    [def],
  )
  const requiredAnswered = useMemo(
    () =>
      def.items.filter(
        (it) => it.required && hasValue(answers[it.id]),
      ).length,
    [def, answers],
  )

  useEffect(() => {
    questionRef.current?.focus({ preventScroll: true })
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return () => {
      if (advanceTimerRef.current !== null) {
        window.clearTimeout(advanceTimerRef.current)
        advanceTimerRef.current = null
      }
    }
  }, [step])

  const progressPct = useMemo(() => {
    if (isIntro) return 0
    if (totalItems === 0) return 100
    if (isReview) return 100
    return Math.round(((step + 1) / totalItems) * 100)
  }, [step, totalItems, isReview, isIntro])

  function set(itemId: number, value: AnswerValue | null) {
    setAnswers((prev) => {
      const empty =
        value === null || value === '' || (Array.isArray(value) && value.length === 0)
      if (empty) {
        const { [itemId]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [itemId]: value }
    })
  }

  function autoAdvance() {
    if (advanceTimerRef.current !== null) {
      window.clearTimeout(advanceTimerRef.current)
    }
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null
      setStep((s) => Math.min(s + 1, totalItems))
    }, 180)
  }

  function back() {
    setStep((s) => Math.max(s - 1, -1))
  }

  function start() {
    setStep(0)
  }

  function next() {
    if (currentItem?.required && !hasValue(answers[currentItem.id])) {
      setError('Respondé esta pregunta antes de continuar.')
      return
    }
    setError(null)
    setStep((s) => Math.min(s + 1, totalItems))
  }

  function skip() {
    setError(null)
    setStep((s) => Math.min(s + 1, totalItems))
  }

  async function submit() {
    const missing = def.items.find(
      (it) => it.required && !hasValue(answers[it.id]),
    )
    if (missing) {
      setError(`Falta el ítem ${missing.id}: ${missing.text}`)
      setStep(def.items.indexOf(missing))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const saved = isEdit
        ? await know.updateResponse(code, answers)
        : await know.submit(code, answers)
      // `onSubmitted` (el que encadena el paso siguiente) lo dispara
      // `SubmitSuccess` cuando termina el acuse, no el POST.
      setSent(saved)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <SubmitSuccess
        heading={isEdit ? 'Cambios guardados' : 'Respuestas enviadas'}
        name={def.name_es}
        onDone={() => onSubmitted(sent)}
      />
    )
  }

  if (isIntro) {
    return (
      <FocusedShell
        def={def}
        isEdit={isEdit}
        isIntro
        step={step}
        totalItems={totalItems}
        isReview={isReview}
        progressPct={progressPct}
        onExit={onCancelEdit}
        onLeave={onLeave}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={start}
              className="btn-primary min-w-0 flex-1 px-md sm:flex-none"
            >
              Comenzar →
            </button>
          </div>
        }
      >
        <IntroScreen def={def} isEdit={isEdit} />
      </FocusedShell>
    )
  }

  const questionTitle = currentItem && (
    <div className="flex flex-col gap-sm px-lg">
      <span
        className={[
          'tag w-fit normal-case tracking-normal',
          currentItem.required ? 'tag-ok' : '',
        ].join(' ')}
      >
        {currentItem.required ? 'Requerida' : 'Opcional'}
      </span>
      <h2 ref={questionRef} tabIndex={-1} className="m-0 font-sans text-lg font-bold normal-case leading-snug tracking-tight text-text-primary outline-none sm:text-xl">
        {currentItem.text}
      </h2>
      {currentItem.help_es && (
        <p className="m-0 text-sm leading-relaxed text-text-secondary">
          {currentItem.help_es}
        </p>
      )}
    </div>
  )

  const body = (
    <div ref={scrollRef} className="flex flex-col gap-lg p-lg">
      {error && <div className="banner-err">{error}</div>}

      {currentItem && (
        <ItemOptions
          key={currentItem.id}
          item={currentItem}
          likert={def.likert}
          value={answers[currentItem.id]}
          onChange={(v) => {
            set(currentItem.id, v)
            if (
              v !== null &&
              (currentItem.kind === 'likert' ||
                currentItem.kind === 'single_select')
            ) {
              autoAdvance()
            }
          }}
        />
      )}

      {isReview && (
        <ReviewCard
          def={def}
          answers={answers}
          onJump={(i) => setStep(i)}
        />
      )}
    </div>
  )

  const footer = (
    <FocusedFooter
      canBack={!submitting}
      onBack={back}
      canSkip={!!currentItem && !currentItem.required && !submitting}
      onSkip={skip}
      isReview={isReview}
      isLastQuestion={step === totalItems - 1}
      submitting={submitting}
      canSubmit={requiredAnswered === requiredCount}
      onNext={next}
      onSubmit={() => void submit()}
      submitLabel={
        submitting
          ? 'Enviando…'
          : isEdit
            ? 'Guardar cambios'
            : 'Enviar respuestas'
      }
    />
  )

  return (
    <FocusedShell
      def={def}
      isEdit={isEdit}
      step={step}
      totalItems={totalItems}
      isReview={isReview}
      progressPct={progressPct}
      onExit={onCancelEdit}
      onLeave={onLeave}
      title={questionTitle}
      footer={footer}
    >
      {body}
    </FocusedShell>
  )
}

// ---------------------------------------------------------------------------
// Intro — instructions shown alone, once, before the first question
// ---------------------------------------------------------------------------

function IntroScreen({
  def,
  isEdit,
}: {
  def: QuestionnaireDefinition
  isEdit: boolean
}) {
  return (
    <div className="flex flex-col gap-lg p-lg text-center">
      <div className="flex flex-wrap items-center justify-center gap-sm">
        <span className="tag">{def.code}</span>
        {def.required_for_vr && !isEdit && (
          <span className="tag-ok normal-case tracking-normal">
            Requerido para entrar al VR
          </span>
        )}
        {isEdit && (
          <span className="tag normal-case tracking-normal">Editando</span>
        )}
      </div>
      <h1 className="m-0 text-2xl font-bold leading-tight tracking-tight text-text-primary sm:text-3xl">
        {def.name_es}
      </h1>
      <p className="m-0 text-lg leading-relaxed text-text-secondary">
        {def.instructions_es}
      </p>
      {def.administration_es && (
        <p className="m-0 text-sm text-text-secondary">
          <strong className="text-text-secondary">Aplicación:</strong>{' '}
          {def.administration_es}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Focused (fullscreen) chrome — only used while taking/editing the form.
// Detail view stays inside KnowLayout so users can navigate freely.
// ---------------------------------------------------------------------------

export function FocusedShell({
  def,
  isEdit,
  isIntro = false,
  step,
  totalItems,
  isReview,
  progressPct,
  onExit,
  onLeave,
  title,
  footer,
  children,
}: {
  def: QuestionnaireDefinition
  isEdit: boolean
  isIntro?: boolean
  step: number
  totalItems: number
  isReview: boolean
  progressPct: number
  /** Si está en edit mode, "Salir" cancela. Si no, `onLeave`. */
  onExit: (() => void) | null
  /** Abandonar el form (cerrar la sesión de la tablet o volver a `exitPath`). */
  onLeave: () => void
  title?: ReactNode
  footer: ReactNode
  children: ReactNode
}) {
  const exit = onExit ?? onLeave

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface">
      <header className="shrink-0 border-b-2 border-primary bg-surface/95 shadow-[0_12px_36px_rgba(226,90,27,0.12)]">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-md px-lg py-md">
          <div className="flex min-w-0 items-center gap-md">
            <VerSportsLogo />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-bold text-text-primary">
                {def.name_es}
              </span>
              <span className="text-xs text-text-muted">
                {def.code}
                {isEdit && ' · editando'}
                {def.required_for_vr && !isEdit && ' · requerido para entrar al VR'}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={exit}
            className="shrink-0 rounded border border-primary/30 bg-transparent px-md py-xs text-xs text-text-secondary hover:border-primary hover:text-text-primary"
          >
            {isEdit ? 'Cancelar' : 'Salir'}
          </button>
        </div>
        <div className="mx-auto w-full max-w-4xl px-lg pb-md">
          <div className="flex items-center gap-md text-xs text-text-secondary">
            <span className="shrink-0 tabular-nums">
              {isIntro ? 'Instrucciones' : isReview ? 'Revisión' : `${step + 1} / ${totalItems}`}
            </span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-highlight ring-1 ring-primary/25">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="shrink-0 tabular-nums">{progressPct}%</span>
          </div>
        </div>
      </header>

      {/* El scroll vive en el `main`, y `min-h-0` es lo que lo hace real: un
          flex item arranca con `min-height: auto`, así que sin eso el main se
          queda del alto de su contenido, el `overflow-y-auto` nunca entra y
          la revisión larga empuja el footer fuera del `fixed inset-0` (bug de
          mg en la tablet, 2026-09-18). `my-auto` centra mientras sobra lugar
          y se resuelve a 0 cuando falta, así que el primer ítem tampoco queda
          arriba del borde, inalcanzable. */}
      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-lg py-lg">
        <div className="mx-auto my-auto flex w-full max-w-2xl flex-col gap-md">
          {title}
          <div className="flex flex-col">{children}</div>
        </div>
      </main>

      <footer className="shrink-0 border-t-2 border-primary bg-surface pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto w-full max-w-3xl px-lg py-md">{footer}</div>
      </footer>
    </div>
  )
}

function FocusedFooter({
  canBack,
  onBack,
  canSkip,
  onSkip,
  isReview,
  isLastQuestion,
  submitting,
  canSubmit,
  onNext,
  onSubmit,
  submitLabel,
}: {
  canBack: boolean
  onBack: () => void
  canSkip: boolean
  onSkip: () => void
  isReview: boolean
  isLastQuestion: boolean
  submitting: boolean
  canSubmit: boolean
  onNext: () => void
  onSubmit: () => void
  submitLabel: string
}) {
  return (
    <div className="flex items-center justify-between gap-sm">
      <button
        type="button"
        onClick={onBack}
        disabled={!canBack}
        className="btn min-w-0 flex-1 px-md sm:flex-none"
      >
        ← Anterior
      </button>
      {isReview ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !canSubmit}
          className="btn-primary min-w-0 flex-[2] sm:flex-none"
        >
          {submitLabel}
        </button>
      ) : (
        <>
          {canSkip && (
            <button
              type="button"
              onClick={onSkip}
              disabled={submitting}
              className="btn-ghost shrink-0 px-md text-xs"
            >
              Saltear
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={submitting}
            className="btn-primary min-w-0 flex-1 px-md sm:flex-none"
          >
            {isLastQuestion ? 'Revisar →' : 'Siguiente →'}
          </button>
        </>
      )}
    </div>
  )
}

function hasValue(v: AnswerValue | undefined): boolean {
  if (v === undefined || v === null) return false
  if (typeof v === 'string') return v.trim() !== ''
  if (Array.isArray(v)) return v.length > 0
  return true
}

// ---------------------------------------------------------------------------
// Item input — branches on `kind`
// ---------------------------------------------------------------------------

export function ItemOptions({
  item,
  likert,
  value,
  onChange,
}: {
  item: ItemDef
  likert: QuestionnaireDefinition['likert']
  value: AnswerValue | undefined
  onChange: (v: AnswerValue | null) => void
}) {
  const rangeHint = [
    item.min_value !== null ? `mín. ${item.min_value}` : null,
    item.max_value !== null ? `máx. ${item.max_value}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  // Escala con anclas solas (SMTQ: sólo los extremos tienen texto). En la
  // lista vertical las opciones del medio quedaban como un cuadro vacío, como
  // si faltara el texto; en fila se leen como los puntos entre dos anclas.
  const anchored = likert.length > 1 && likert.some((opt) => !opt.label)

  return (
    <div className="flex flex-col gap-lg">
      {item.kind === 'likert' && anchored && (
        <div className="flex flex-col gap-sm">
          <div className="flex gap-sm">
            {likert.map((opt) => {
              const selected = value === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-label={opt.label ? `${opt.value} · ${opt.label}` : String(opt.value)}
                  onClick={() => onChange(opt.value)}
                  className={[
                    'choice-card flex-1 justify-center font-mono text-lg',
                    selected ? 'choice-card-selected' : '',
                  ].join(' ')}
                >
                  {opt.value}
                </button>
              )
            })}
          </div>
          <div className="flex justify-between gap-md text-sm text-text-muted">
            <span>{likert[0]!.label}</span>
            <span className="text-right">{likert[likert.length - 1]!.label}</span>
          </div>
        </div>
      )}

      {item.kind === 'likert' && !anchored && (
        <div className="flex flex-col gap-sm">
          {likert.map((opt) => {
            const selected = value === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={[
                  'choice-card',
                  selected ? 'choice-card-selected' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono text-sm',
                    selected
                      ? 'border-primary bg-primary text-primary-fg'
                      : 'border-outline text-text-muted',
                  ].join(' ')}
                >
                  {opt.value}
                </span>
                <span className="flex-1">{opt.label}</span>
                {selected && (
                  <span className="text-xs font-bold uppercase tracking-wide">
                    Elegida
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {item.kind === 'single_select' && (
        <div className="flex flex-col gap-sm">
          {item.options.map((opt) => {
            const selected = value === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={[
                  'choice-card',
                  selected ? 'choice-card-selected' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                    selected ? 'border-primary bg-primary' : 'border-outline',
                  ].join(' ')}
                />
                <span className="flex-1">{opt.label}</span>
                {selected && (
                  <span className="text-xs font-bold uppercase tracking-wide">
                    Elegida
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {item.kind === 'multi_select' && (
        <div className="flex flex-col gap-sm">
          {item.options.map((opt) => {
            const current = Array.isArray(value) ? value : []
            const selected = current.includes(opt.value)
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  onChange(
                    selected
                      ? current.filter((x) => x !== opt.value)
                      : [...current, opt.value],
                  )
                }
                className={[
                  'choice-card',
                  selected ? 'choice-card-selected' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs',
                    selected ? 'border-primary bg-primary text-primary-fg' : 'border-outline',
                  ].join(' ')}
                >
                  {selected ? '✓' : ''}
                </span>
                <span className="flex-1">{opt.label}</span>
              </button>
            )
          })}
          <p className="m-0 text-xs text-text-muted">Podés elegir más de una.</p>
        </div>
      )}

      {item.kind === 'date' && (
        <div className="w-full max-w-xs">
          <input
            type="date"
            className="input w-full text-lg tabular-nums"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
            autoFocus
          />
        </div>
      )}

      {item.kind === 'number' && (
        <div className="w-full max-w-sm rounded-md border border-primary/30 bg-surface-highlight/40 p-md">
          <div className="flex items-baseline gap-md">
            <input
              type="number"
              className="input w-full max-w-[140px] text-lg font-bold tabular-nums"
              value={typeof value === 'number' ? value : (value ?? '')}
              min={item.min_value ?? undefined}
              max={item.max_value ?? undefined}
              inputMode="numeric"
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  onChange(null)
                  return
                }
                const n = Number(raw)
                onChange(Number.isFinite(n) ? n : null)
              }}
              autoFocus
            />
            {item.unit && (
              <span className="text-sm text-text-muted">{item.unit}</span>
            )}
          </div>
          {rangeHint && (
            <p className="mt-sm text-xs text-text-muted">{rangeHint}</p>
          )}
        </div>
      )}

      {item.kind === 'slider' && (
        <div className="w-full max-w-xl rounded-md border border-primary/30 bg-surface-highlight/40 p-lg">
          <div className="mb-sm flex items-center justify-between text-xs text-text-muted">
            <span>{item.min_label ?? (item.min_value ?? 0)}</span>
            <span className="font-mono text-base font-bold text-primary tabular-nums">
              {typeof value === 'number' ? value : '—'}
              {item.unit ? ` ${item.unit}` : ''}
            </span>
            <span>{item.max_label ?? (item.max_value ?? 10)}</span>
          </div>
          <input
            type="range"
            className="w-full accent-primary"
            min={item.min_value ?? 0}
            max={item.max_value ?? 10}
            step={item.step ?? 1}
            value={typeof value === 'number' ? value : (item.min_value ?? 0)}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          {typeof value !== 'number' && (
            <p className="mt-sm text-xs text-text-muted">Mové el control para responder.</p>
          )}
        </div>
      )}

      {item.kind === 'text' && (
        <div className="flex flex-col gap-xs">
          <textarea
            className="input min-h-[140px] resize-y text-base leading-relaxed"
            value={typeof value === 'string' ? value : ''}
            maxLength={item.max_length}
            onChange={(e) =>
              onChange(e.target.value === '' ? null : e.target.value)
            }
            placeholder="Escribí tu respuesta acá…"
            autoFocus
          />
          {item.unit && (
            <span className="text-lg text-text-muted">{item.unit}</span>
          )}
          {item.max_length > 0 && (
            <div className="self-end text-xs text-text-muted">
              {typeof value === 'string' ? value.length : 0} / {item.max_length}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Review (last step before submit)
// ---------------------------------------------------------------------------

function ReviewCard({
  def,
  answers,
  onJump,
}: {
  def: QuestionnaireDefinition
  answers: Record<number, AnswerValue>
  onJump: (step: number) => void
}) {
  const answered = def.items.filter((it) => hasValue(answers[it.id])).length

  return (
    <div className="flex flex-col gap-md">
      <div className="rounded-md border border-primary/40 bg-primary/10 p-lg">
        <div className="text-xs uppercase tracking-wide text-primary">
          Último paso
        </div>
        <h2 className="m-0 mt-xs normal-case tracking-normal text-text-primary">
          Revisá tus respuestas
        </h2>
        <p className="mt-sm text-sm text-text-muted">
          Completaste {answered} de {def.items.length} ítems. Tocá cualquiera
          para corregirlo antes de enviar.
        </p>
      </div>
      <ol className="m-0 grid grid-cols-1 gap-sm p-0">
        {def.items.map((it, i) => {
          const v = answers[it.id]
          const blank = !hasValue(v)
          return (
            <li
              key={it.id}
              className="rounded-md border border-primary/20 bg-surface-elevated p-md shadow-[0_0_20px_rgba(226,90,27,0.06)]"
            >
              <button
                type="button"
                onClick={() => onJump(i)}
                className="flex w-full items-start justify-between gap-md text-left text-sm hover:text-primary"
              >
                <span className="min-w-0">
                  <span className="font-mono text-xs text-text-muted">
                    {it.id}.
                  </span>{' '}
                  <span>{it.text}</span>
                  <span className="mt-xs block text-xs text-text-muted">
                    {blank || v === undefined
                      ? it.required
                        ? 'Falta responder'
                        : 'Sin respuesta'
                      : formatAnswer(it, v, def.likert)}
                  </span>
                </span>
                <span
                  className={[
                    'tag shrink-0 normal-case tracking-normal',
                    blank
                      ? it.required
                        ? 'tag-err'
                        : ''
                      : 'tag-ok',
                  ].join(' ')}
                >
                  {blank ? (it.required ? 'Falta' : 'Opcional') : 'Listo'}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function formatAnswer(
  it: ItemDef,
  v: AnswerValue,
  likert: QuestionnaireDefinition['likert'],
): string {
  if (it.kind === 'likert') {
    const opt = likert.find((o) => o.value === v)
    if (!opt) return String(v)
    return opt.label ? `${opt.value} · ${opt.label}` : String(opt.value)
  }
  if (it.kind === 'single_select') {
    const opt = it.options.find((o) => o.value === v)
    return opt ? opt.label : String(v)
  }
  if (it.kind === 'multi_select') {
    const chosen = Array.isArray(v) ? v : []
    const labels = chosen.map((val) => it.options.find((o) => o.value === val)?.label ?? val)
    return labels.join(', ') || '—'
  }
  if (it.kind === 'number' || it.kind === 'slider') {
    return it.unit ? `${v} ${it.unit}` : String(v)
  }
  // date + text — string. text truncated so the review row stays single-line.
  const s = String(v)
  return s.length > 40 ? `${s.slice(0, 37)}…` : s
}

// ---------------------------------------------------------------------------
// Detail (already responded)
// ---------------------------------------------------------------------------

function DetailView({
  def,
  response,
  justSubmitted,
  onEdit,
}: {
  def: QuestionnaireDefinition
  response: ResponseDetail
  justSubmitted: boolean
  onEdit: (() => void) | null
}) {
  // Players never see their own scores here — psychometric results (SMTQ,
  // CSAI-2R) are delivered personally by the sport psychologist from the
  // admin view (AdminQuestionnaireDetail), not self-served in the app.
  return (
    <section>
      <div className="rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]">
        <p className="m-0 text-sm text-text-secondary">
          <Link to="/">← Inicio</Link>
        </p>
        <span className="tag mt-md">{def.code}</span>
        <h1 className="mt-sm break-words">{def.name_es}</h1>
      </div>

      <div className="banner-ok flex flex-col gap-md">
        <div className="flex flex-wrap items-center justify-between gap-md">
          <span>
            {justSubmitted
              ? '¡Gracias! Tu respuesta quedó registrada.'
              : 'Ya respondiste este cuestionario.'}{' '}
            <span className="text-text-muted">
              (
              {new Date(response.submitted_at).toLocaleString('es-AR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
              )
            </span>
          </span>
          {onEdit && (
            <button type="button" onClick={onEdit} className="btn text-xs">
              Editar respuestas
            </button>
          )}
        </div>
        {def.code === PRE_SESSION_CODE ? (
          // El chequeo previo no tiene puntaje ni clave de corrección: son
          // tres datos de contexto que el jugador acaba de dar, así que
          // mostrarlos es simplemente devolverle lo suyo. De sólo lectura:
          // se corrige respondiendo el de la próxima sesión, no editando este.
          <PreSessionAnswers def={def} response={response} />
        ) : (
          <p className="m-0 text-sm text-text-secondary">
            Acá no se muestran puntajes ni resultados: el equipo los revisa y
            te da la devolución personalmente.
          </p>
        )}
        {justSubmitted && (
          <Link to="/" className="btn-primary self-start no-underline">
            Volver al inicio →
          </Link>
        )}
      </div>
    </section>
  )
}

/**
 * Las tres respuestas del chequeo previo, tal como quedaron. El backend
 * indexa las answers por `Item.key` (`sleep_hours`, `caffeine_last_2h`,
 * `hours_since_physical_training`) para esta definición, con el id del ítem
 * como respaldo del formato general.
 */
function PreSessionAnswers({
  def,
  response,
}: {
  def: QuestionnaireDefinition
  response: ResponseDetail
}) {
  const answers = response.answers ?? {}
  const rows = def.items
    .map((it) => {
      const raw = answers[it.key ?? ''] ?? answers[String(it.id)]
      return raw === undefined || raw === null
        ? null
        : { id: it.id, text: it.text, value: formatAnswer(it, raw, def.likert) }
    })
    .filter((r): r is { id: number; text: string; value: string } => r !== null)

  if (rows.length === 0) {
    return (
      <p className="m-0 text-sm text-text-secondary">
        Este chequeo se respondió en el casco, antes de la sesión.
      </p>
    )
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-xs p-0">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-sm text-sm">
          <span className="text-text-secondary">{r.text}</span>
          <span className="font-medium text-text-primary">{r.value}</span>
        </li>
      ))}
    </ul>
  )
}
